import React from "react";
import StatusBadge from "../common/StatusBadge";
import RoomChip from "../common/RoomChip";
import { deviceDisplayName, deviceNameOnly } from "../../utils/deviceName";

/**
 * One column definition shared by the Devices tab and Room detail, so the two
 * cannot describe the same device differently. RoomDetail passes
 * `hideRoomColumn` because every row there is in the same room.
 *
 * @param navigate  react-router navigate
 * @param basePath  e.g. `/admin/dash/users/<id>` — links are built from it
 */
export function buildDeviceColumns({
  navigate,
  basePath,
  hideRoomColumn = false,
  // When given, clicks open a modal instead of pushing a route. The route
  // fallback is kept so the /devices/:id and /rooms/:id pages (deep links)
  // still work with the same column definitions.
  onOpenRoom,
  onOpenSchedules,
} = {}) {
  const columns = [
    {
      id: "device",
      header: "Device",
      cell: ({ row }) => {
        const d = row.original;
        return (
          <div className="min-w-[180px]">
            <p
              className={`font-semibold ${
                d.status === "online" ? "text-slate-800" : "text-slate-500"
              }`}
            >
              {deviceDisplayName(d)}
            </p>
            {/* The provider's model, when it reported one — it is what tells
                a dimmer from a plain switch at a glance. */}
            {typeof d.meta?.model === "string" && d.meta.model && (
              <p className="mt-0.5 text-[11px] text-slate-500">{d.meta.model}</p>
            )}
            {/* The vendor id, shown because it is what schedules reference —
                unless the heading above already IS the id (no name). */}
            {deviceNameOnly(d) && (
              <p className="mt-0.5 break-all font-mono text-[11px] text-slate-400">
                {d.deviceId}
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: "brand",
      header: "Brand",
      cell: ({ row }) => (
        <span className="text-sm capitalize text-slate-700">
          {row.original.brand || "—"}
        </span>
      ),
    },
    {
      id: "connectivity",
      header: "Connectivity",
      // ⚠️ A missing `status` is unknown, not offline — Device.status has no
      // "unknown" value in its enum, so absence is the only way it can appear.
      cell: ({ row }) => (
        <StatusBadge kind="deviceStatus" value={row.original.status} />
      ),
    },
    {
      id: "state",
      header: "State",
      cell: ({ row }) => (
        <StatusBadge kind="powerState" value={row.original.deviceOn} unknownLabel="—" />
      ),
    },
    {
      id: "added",
      header: "Added",
      cell: ({ row }) => (
        <StatusBadge kind="isAdded" value={row.original.isAdded !== false} />
      ),
    },
  ];

  if (!hideRoomColumn) {
    columns.push({
      id: "room",
      header: "Room",
      cell: ({ row }) => {
        const room = row.original.roomId;
        return (
          <RoomChip
            room={room}
            onClick={
              room?._id
                ? () =>
                    onOpenRoom
                      ? onOpenRoom(room)
                      : navigate?.(`${basePath}/rooms/${room._id}`)
                : undefined
            }
          />
        );
      },
    });
  }

  columns.push(
    {
      id: "smartRestore",
      header: "Smart Restore",
      cell: ({ row }) => {
        const sr = row.original.smartRestore;
        if (!sr?.enabled) return <span className="text-sm text-slate-400">Off</span>;
        return (
          <div className="text-sm">
            <span className="font-medium text-indigo-600">✨ On</span>
            {sr.expectedState != null && (
              <p className="text-[11px] text-slate-500">
                expects {sr.expectedState ? "ON" : "OFF"}
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: "schedules",
      header: "Schedules",
      cell: ({ row }) => {
        const d = row.original;
        const active = d.activeScheduleCount ?? 0;
        const total = d.totalScheduleCount ?? active;

        if (!total) {
          return <span className="text-sm text-slate-400">Not in any schedule</span>;
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              // ⚠️ VENDOR id — Schedule.actions[].deviceId stores this string,
              // never Device._id.
              if (onOpenSchedules) onOpenSchedules(d);
              else
                navigate?.(
                  `${basePath}/schedules?deviceId=${encodeURIComponent(d.deviceId)}`
                );
            }}
            className="text-sm font-medium text-indigo-600 hover:underline"
          >
            In {active} active
            {total !== active && (
              <span className="text-slate-400"> · {total} total</span>
            )}
          </button>
        );
      },
    }
  );

  return columns;
}

export default buildDeviceColumns;
